import { useQuery } from "@tanstack/react-query";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { agents } from "~/modules/analytics/lib/agents";

/** `capabilities` names each capability once per time it will be invoked. */
export function AgentEstimate({ entity, capabilities }: { entity: string; capabilities: string[] }) {
	const t = useT();
	const { locale } = useI18n();
	// A caller's array literal is a new object every render; its contents are
	// the dependency, not its identity.
	const input = JSON.stringify(capabilities);
	const state = useQuery({
		queryKey: [...keys.entity(entity), "capability-estimate", input],
		queryFn: () => agents.estimate(entity, JSON.parse(input)),
		...read,
		// Another set of capabilities is another question, and the last
		// estimate is not a stand-in for its answer.
		placeholderData: undefined,
	});
	if (state.isFetching && state.data === undefined)
		return (
			<p className="hint" role="status">
				{t("agents.estimateLoading")}
			</p>
		);
	if (state.error || !state.data) return <p className="hint">{t("agents.estimateUnavailable")}</p>;
	const estimate = state.data;
	return (
		<div className="stack">
			<p>
				{t("agents.estimate")}:{" "}
				<strong>
					{estimate.reference_credits === null
						? t("agents.estimateUnknown")
						: t("agents.estimateCredits", { n: Math.ceil(estimate.reference_credits) })}
				</strong>
			</p>
			<p className="hint">{t("agents.estimateBasis")}</p>
			{estimate.lines
				.filter((line) => line.fee !== "free")
				.map((line) => (
					<p className="hint" key={line.capability}>
						{pick(locale, line.label)}:{" "}
						{t("agents.estimateLine", { n: line.max_invocations, samples: line.observations })} ·{" "}
						{line.reference_credits === null
							? t("agents.estimateUnknown")
							: t("agents.estimateCredits", { n: Math.ceil(line.reference_credits) })}
					</p>
				))}
		</div>
	);
}
